import { formatDate } from "@theprelude/core";
import {
  ScaleLinear,
  ScaleTime,
  Selection,
  axisBottom,
  axisLeft,
  bisector,
  line,
  pointer,
  scaleLinear,
  scaleUtc,
  select,
  utcDay,
} from "d3";

export const nFormatter = (num: number, digits: number) => {
  const lookup = [
    { value: 1, symbol: "" },
    { value: 1e3, symbol: "k" },
  ];
  const rx = /\.0+$|(\.\d*[1-9])0+$/;
  const item = [...lookup].reverse().find(function (item) {
    return num >= item.value;
  });
  return item
    ? (num / item.value).toFixed(digits).replace(rx, "$1") + item.symbol
    : "0";
};

// Set the dimensions and margins of the graph
const GRAPH_MARGIN = { top: 6, right: 0, bottom: 16, left: 20 };

export type Point = {
  date: Date;
  value: number | null;
};

interface GraphProps {
  data: Point[];
  benchmarkData: Point[];
  finish: Date;
  node: HTMLDivElement;
  start: Date;
  tooltip: {
    clear: () => void;
    set: (p: Point, b: Point) => void;
  };
}

export function getStatusColor(percent: number) {
  if (percent === 100) {
    return "rgb(var(--status-green))";
  }

  if (percent >= 60) {
    return "rgb(var(--status-yellow))";
  }

  return "rgb(var(--status-red))";
}

export class LineChart {
  data: GraphProps["data"];
  benchmarkData: GraphProps["data"];
  dates: Date[];
  graph!: Selection<SVGGElement, unknown, null, undefined>;
  graphHeight: number;
  graphWidth: number;
  node: GraphProps["node"];
  tooltip: GraphProps["tooltip"];
  xScale!: ScaleTime<number, number, never>;
  yScale!: ScaleLinear<number, number, never>;

  constructor({
    data,
    node,
    start,
    finish,
    tooltip,
    benchmarkData,
  }: GraphProps) {
    this.node = node;
    this.data = data;
    this.benchmarkData = benchmarkData;

    this.dates = [start, finish];
    this.tooltip = tooltip;

    // Set dimensions and margins of graph
    const nodeWidth = node.clientWidth;
    const nodeHeight = node.clientHeight;
    this.graphWidth = nodeWidth - GRAPH_MARGIN.left + GRAPH_MARGIN.right;
    this.graphHeight = nodeHeight - GRAPH_MARGIN.top - GRAPH_MARGIN.bottom;

    this.create();
    this.setX();
    this.setY();
    this.drawLine();
    this.drawBenchmarkLine();
    this.drawMouseOver();
  }

  /* Removes the elements from the graph */
  cleanup() {
    select(this.node).selectAll("*").remove();
  }

  /* Appends graph to element */
  private create() {
    this.graph = select(this.node)
      .append("svg")
      .attr("width", this.graphWidth + GRAPH_MARGIN.left)
      .attr("height", this.graphHeight + GRAPH_MARGIN.top + GRAPH_MARGIN.bottom)
      .append("g")
      .attr(
        "transform",
        "translate(" + GRAPH_MARGIN.left + "," + GRAPH_MARGIN.top + ")",
      );
  }

  /* Sets the horizontal axis, formats the dates, and specifies frequency of x-axis ticks. */
  private setX() {
    let ticks = utcDay.every(1);

    if (this.node.clientWidth < 1000) {
      ticks = utcDay.every(2);
    }

    if (this.node.clientWidth < 750) {
      ticks = utcDay.every(4);
    }

    this.xScale = scaleUtc()
      .range([
        GRAPH_MARGIN.left,
        this.graphWidth - GRAPH_MARGIN.right - GRAPH_MARGIN.left,
      ])
      .domain(this.dates);

    const xAxis = axisBottom(this.xScale)
      .tickFormat((d) => {
        return formatDate(new Date(d.valueOf()), "MMM d", "UTC");
      })
      .ticks(ticks);

    // x-axis
    this.graph
      .append("g")
      .attr(
        "transform",
        `translate(0, ${this.graphHeight - GRAPH_MARGIN.bottom})`,
      )
      .style("font-size", () => {
        return "10px";
      })
      .attr("id", "x-axis")
      .call(xAxis)
      .call((g) => g.select(".domain").remove())
      .call((g) =>
        g.selectAll(".tick line").attr("stroke", "rgb(var(--stroke-line))"),
      )
      .call((g) =>
        g
          .selectAll(".tick text")
          .attr("fill", "rgb(var(--secondary-foreground))"),
      );
  }

  private setY() {
    const maxEndpoints = 100;

    this.yScale = scaleLinear()
      .domain([0, maxEndpoints])
      .range([this.graphHeight - GRAPH_MARGIN.bottom, GRAPH_MARGIN.top]);

    const tickValues = [
      0,
      0.25 * maxEndpoints,
      0.5 * maxEndpoints,
      0.75 * maxEndpoints,
      maxEndpoints,
    ];

    const yAxis = axisLeft(this.yScale)
      .tickValues(tickValues)
      .tickFormat((d, index) => {
        if ((index !== 0 && d.valueOf() === 0) || d.valueOf() % 1 !== 0) {
          return "";
        }
        return nFormatter(d.valueOf(), 1);
      });

    const yAxisGrid = axisLeft(this.yScale)
      .tickFormat(() => "")
      .tickSize(-this.graphWidth)
      .tickValues(tickValues);

    // y-axis
    this.graph
      ?.append("g")
      .attr("transform", `translate(${GRAPH_MARGIN.left}, 0)`)
      .attr("id", "y-axis")
      .call(yAxis)
      .call((g) => g.select(".domain").remove())
      .call((g) => g.selectAll(".tick line").remove())
      .call((g) =>
        g
          .selectAll(".tick text")
          .attr("fill", "rgb(var(--secondary-foreground))")
          .style("font-size", () => {
            return "10px";
          }),
      );

    this.graph
      ?.append("g")
      .attr("class", "x axis-grid")
      .attr(
        "transform",
        // eslint-disable-next-line prettier/prettier
        `translate(${GRAPH_MARGIN.left}, 0) scale(${
          (this.graphWidth - GRAPH_MARGIN.left * 2) / this.graphWidth
        }, 1)`,
      )
      .call(yAxisGrid)
      .call((g) => g.select(".domain").remove())
      .call((g) =>
        g
          .selectAll(".tick line")
          .attr("stroke", "rgb(var(--stroke-line))")
          .attr("stroke-width", "1"),
      );
  }

  private drawLine() {
    this.data.forEach((d, index) => {
      const next = this.data[index + 1];
      this.graph
        .append("path")
        .datum(this.data.slice(index, index + 2))
        .attr("fill", "none")
        .attr("stroke", "rgb(var(--primary-foreground))")
        .attr("stroke-width", 1)
        .attr("stroke-dasharray", next?.value === null ? "3, 5" : "1, 0")
        .attr(
          "d",
          line<Point>()
            .x((d) => this.xScale(d.date))
            .y((d) => this.yScale(d.value ?? 0)),
        );
    });
  }

  private drawBenchmarkLine() {
    this.graph
      .append("path")
      .datum(this.benchmarkData)
      .attr("fill", "none")
      .attr("stroke", "rgb(var(--secondary-foreground))")
      .attr("stroke-width", 1)
      .attr("stroke-dasharray", "3, 5")
      .attr(
        "d",
        line<Point>()
          .x((d) => this.xScale(d.date))
          .y((d) => this.yScale(d.value ?? 0)),
      );
  }

  // Draws a vertical line on the graph at the current date
  private drawMouseOver() {
    const mouseG = this.graph
      .append("g")
      .attr("class", "mouse-over-effects")
      .attr("opacity", 0);

    mouseG
      .append("line")
      .attr("class", "mouse-line")
      .attr("stroke", "rgb(var(--stroke-line))")
      .attr("stroke-width", 1)
      .attr("x1", 0)
      .attr("x2", 0)
      .attr("y1", this.graphHeight - GRAPH_MARGIN.bottom)
      .attr("y2", GRAPH_MARGIN.top);

    const circle = mouseG
      .append("circle")
      .attr("r", 4)
      .attr("fill", "rgb(var(--status-yellow))")
      .attr("stroke", "rgb(var(--primary-foreground))");

    const bisect = bisector<Point, any>((d) => d.date).center;
    select(this.node)
      .on("mousemove", (e: MouseEvent) => {
        const [x] = pointer(e, this.node);
        const i = bisect(this.data, this.xScale.invert(x - GRAPH_MARGIN.left));
        const point = this.data[i];

        if (point && point.value !== null) {
          const x = this.xScale(point.date);
          const y = this.yScale(point.value);
          circle
            .attr("cx", x)
            .attr("cy", y)
            .attr("fill", getStatusColor(point.value));

          mouseG
            .select(".mouse-line")
            .attr("x1", x)
            .attr("x2", x)
            .attr("y1", GRAPH_MARGIN.top)
            .attr("y2", this.graphHeight - GRAPH_MARGIN.bottom)
            .attr("opacity", 1);

          const p = this.benchmarkData[i];

          if (p) {
            this.tooltip.set(point, p);
          }
        } else {
          mouseG.attr("opacity", 0);
          this.tooltip.clear();
        }
      })
      .on("mouseout", () => {
        mouseG.attr("opacity", 0);
        this.tooltip.clear();
      })
      .on("mouseover", () => {
        mouseG.attr("opacity", 1);
      });
  }
}
